import { ref } from 'vue';
import api from '@/api';
import {
  ContentsContext,
  IndustryData,
  SearchData,
  TrackingBaseData,
} from '@/api/tracking';
import { AxiosError } from 'axios';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { AdpDocument, ArticleMarkSource, UserDetailEntity } from '@/types';

type CreateMarkProps = {
  adpDocument: AdpDocument;
  userInfo?: UserDetailEntity;
  themeId?: number | null;
  tags?: string[];
  memo?: string;
  afterUndo?: () => void;
  hideSnackbar?: boolean;
};

type UpdateMarkProps = {
  adpDocument: AdpDocument;
  userInfo?: UserDetailEntity;
};

type RemoveMarkProps = {
  adpDocument: AdpDocument;
  userInfo?: UserDetailEntity;
  afterUndo?: () => void;
  hideSnackbar?: boolean;
};

export const useMark = (props: {
  // 計測
  markSource?: ArticleMarkSource;
  trackingBaseData: TrackingBaseData;
  rankInWholeFeed?: number;
  searchData?: SearchData;
  industryData?: IndustryData;
  trendCompanyText?: string;
  contentsContext?: ContentsContext;
  execution?: string;
}) => {
  const { createSnackbar } = useSnackbar();

  const isProcessing = ref(false);

  const createMark = async ({
    adpDocument,
    userInfo,
    themeId,
    tags,
    memo,
    hideSnackbar,
  }: CreateMarkProps) => {
    if (isProcessing.value) return;
    isProcessing.value = true;
    try {
      const createdMark = await api.sendMark(
        adpDocument,
        themeId ?? null,
        props.markSource,
        props.trackingBaseData,
        props.rankInWholeFeed,
        props.searchData,
        props.industryData,
        props.trendCompanyText,
        props.contentsContext,
        props.execution,
      );

      await updateTagsAndMemo({
        adpDocument,
        tags,
        memo,
        isCreate: true,
        hideSnackbar: true,
      });

      adpDocument.marks.push({
        ...createdMark,
        user_name: userInfo?.user_name ?? '',
      });

      if (!hideSnackbar) {
        createSnackbar({
          type: 'link',
          message: 'マイページ',
          params: {
            link: `/mypage/marks`,
            suffixText: 'にマークしました',
          },
        });
      }
      return true;
    } catch (e) {
      if (!hideSnackbar) {
        createSnackbar({
          message: 'マークできませんでした',
          type: 'error',
        });
      }
      throw e;
    } finally {
      isProcessing.value = false;
    }
  };

  const updateMark = async ({ adpDocument, userInfo }: UpdateMarkProps) => {
    const targetMark = adpDocument.marks.find(
      mark => mark.user_id === userInfo?.id,
    );
    if (!targetMark) return;

    const updatedMark = await api.updateMark(targetMark.id);

    const index = adpDocument.marks.findIndex(m => m.user_id === userInfo?.id);
    adpDocument.marks[index] = updatedMark;
  };

  const updateTagsAndMemo = async ({
    adpDocument,
    tags,
    memo,
    isCreate = false,
    hideSnackbar,
  }: CreateMarkProps & {
    isCreate?: boolean;
  }) => {
    // 作成の場合は、tagsとmemoの比較が不要
    // 理由：マークとグループマークのtagsとmemoはadpDocumentの同じキーに保存されるので
    //      マークとグループマークのタグとメモが同じになる時、更新するリクエストを投げていない
    const tagMemoRequests: Promise<void>[] = [];
    try {
      const isUpdatingTags =
        tags &&
        tags.length > 0 &&
        (isCreate || tags?.toString() !== adpDocument.tags?.toString());
      const isRemovingAllTags =
        tags !== undefined &&
        tags.length === 0 &&
        adpDocument.tags &&
        adpDocument.tags?.length > 0;
      if (isUpdatingTags) {
        tagMemoRequests.push(api.updateUserArticleTags(adpDocument, tags));
        adpDocument.tags = [...tags];
      } else if (isRemovingAllTags) {
        tagMemoRequests.push(api.updateUserArticleTags(adpDocument, []));
        adpDocument.tags = [];
      }

      const isUpdatingMemo =
        memo && memo.length > 0 && (isCreate || memo !== adpDocument.memo);
      const isRemovingMemo =
        memo !== undefined &&
        memo.length === 0 &&
        adpDocument.memo &&
        adpDocument.memo?.length > 0;
      if (isUpdatingMemo) {
        tagMemoRequests.push(api.updateUserArticleMemo(adpDocument, memo));
        adpDocument.memo = memo;
      } else if (isRemovingMemo) {
        tagMemoRequests.push(
          api.deleteUserArticleMemo(adpDocument).catch((e: AxiosError) => {
            if (e.response?.status !== 404) {
              throw e;
            }
          }),
        );
        adpDocument.memo = '';
      }

      if (
        isUpdatingTags ||
        isRemovingAllTags ||
        isUpdatingMemo ||
        isRemovingMemo
      ) {
        await Promise.all(tagMemoRequests);
        await api.trackEvent(
          'set_article_option',
          props.trackingBaseData,
          adpDocument,
          undefined,
          {
            tags: tags,
            memo: memo,
          },
        );
      }

      if (!hideSnackbar) {
        createSnackbar({
          message: '記事情報を更新しました',
          type: 'success',
        });
      }
    } catch (e) {
      if (!hideSnackbar) {
        createSnackbar({
          message: '記事情報を更新できませんでした',
          type: 'error',
        });
      }
      throw e;
    }
  };

  const removeMark = async ({
    adpDocument,
    userInfo,
    afterUndo,
    hideSnackbar,
  }: RemoveMarkProps) => {
    const targetMark = adpDocument.marks.find(
      mark => mark.user_id === userInfo?.id,
    );
    if (!targetMark) return;
    if (isProcessing.value) return;
    isProcessing.value = true;

    try {
      await api.deleteMark(targetMark.id);

      adpDocument.marks = adpDocument.marks.filter(m => m.id !== targetMark.id);

      if (!hideSnackbar) {
        createSnackbar({
          message: 'マークを取り消しました',
          type: 'success',
          undoFunc: async () => {
            const created = await createMark({
              adpDocument,
              userInfo,
              hideSnackbar,
            });
            if (afterUndo && created) {
              afterUndo();
            }
          },
        });
      }
      return true;
    } catch (e) {
      if (!hideSnackbar) {
        createSnackbar({
          message: 'マークを取り消せませんでした',
          type: 'error',
        });
      }
      throw e;
    } finally {
      isProcessing.value = false;
    }
  };

  return {
    createMark,
    updateTagsAndMemo,
    updateMark,
    removeMark,
    isProcessing,
  };
};
